import { useMemo, useState } from 'react'
import { TableCell, TableRow } from '@/components/ui/table'
import { Card } from '@/components/ui/card'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { Badge } from '@/components/ui/badge'
import DateRangePicker from '@/pages/Dashboard/Partials/DateRangePicker'

import Search from '@/assets/icons/Search'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Skeleton } from '@/components/ui/skeleton'
import { useFetchForecasts } from '../hooks/useFetchForecasts'
import TableListCustom from '@/components/shared/TableListCustom/TableListCustom'

const isReceivableEntryType = (entryType?: string) => entryType === 'receivable'
const isRetentionEntryType = (entryType?: string) =>
  entryType === 'retention' || entryType === 'forced_retention'

const CustomTooltip = ({ active, payload, label, currency }: any) => {
  const { t } = useTranslation('wallet')

  if (active && payload && payload.length) {
    const row = payload?.[0]?.payload || {}
    const releaseLabel = row.release_label ?? row.label ?? '-'
    const retentionLabel = row.retention_label ?? '-'

    return (
      <div className="bg-[#27272A] p-3 rounded-lg border border-zinc-700">
        <p className="text-zinc-400 text-xs mb-1">{label}</p>
        <div className="flex items-center gap-1">
          <span className="text-sm">{t('release')}</span>
          <span className="text-primary font-medium">{releaseLabel}</span>
        </div>
        {/* <div className='flex items-center gap-1'>
          <span className='text-sm'>Retenção</span>
          <span className='text-primary font-medium'>
            {retentionLabel}
          </span>
        </div> */}
      </div>
    )
  }
  return null
}
const parseMonetaryValue = (value: string): number => {
  if (!value) return 0

  let cleanValue = value.replace(/R\$\s?/g, '')

  const isNegative =
    cleanValue.includes('-') ||
    (cleanValue.includes('(') && cleanValue.includes(')'))
  cleanValue = cleanValue.replace(/[^\d,.-]/g, '')

  if (cleanValue.includes(',') && !cleanValue.includes('.')) {
    cleanValue = cleanValue.replace(',', '.')
  } else if (cleanValue.includes('.') && cleanValue.includes(',')) {
    cleanValue = cleanValue.replace(/\./g, '').replace(',', '.')
  }

  const numericValue = parseFloat(cleanValue) || 0
  return isNegative ? -Math.abs(numericValue) : numericValue
}

const formatYAxisValue = (value: number): string => {
  const absValue = Math.abs(value)
  const sign = value < 0 ? '-' : ''

  if (absValue >= 1000000) {
    return `${sign}R$ ${(absValue / 1000000).toFixed(1)}M`
  } else if (absValue >= 1000) {
    return `${sign}R$ ${(absValue / 1000).toFixed(1)}K`
  } else {
    return `${sign}R$ ${absValue.toFixed(0)}`
  }
}

const getRelativeTime = (dateString: string, t: any): string => {
  const [datePart, timePart] = dateString.split(' ')
  const [day, month, year] = datePart.split('/')
  const [hours, minutes] = timePart.split(':')

  const scheduledDate = new Date(
    parseInt(year),
    parseInt(month) - 1,
    parseInt(day),
    parseInt(hours),
    parseInt(minutes),
  )

  const now = new Date()
  const diffMs = scheduledDate.getTime() - now.getTime()
  const diffMinutes = Math.round(diffMs / (1000 * 60))

  if (diffMinutes < 0) {
    const absDiff = Math.abs(diffMinutes)
    if (absDiff < 60) return `${t('ago')} ${absDiff}${t('minutes')}`
    if (absDiff < 1440)
      return `${t('ago')} ${Math.floor(absDiff / 60)}${t('hours')}`
    return `${t('ago')} ${Math.floor(absDiff / 1440)}${t('days')}`
  }

  if (diffMinutes < 60) return `${t('in')} ${diffMinutes}${t('minutes')}`
  if (diffMinutes < 1440)
    return `${t('in')} ${Math.floor(diffMinutes / 60)}${t('hours')}`
  return `${t('in')} ${Math.floor(diffMinutes / 1440)}${t('days')}`
}

export default function TransactionList({ currency }: { currency: string }) {
  const { t } = useTranslation('wallet')
  const navigate = useNavigate()
  const [currentPage, setCurrentPage] = useState(1)
  const [dateRange, setDateRange] = useState<{
    from: string
    to: string
  } | null>(null)

  const getEntryTypeBadgeClassName = (entryType?: string) => {
    switch (entryType) {
      case 'receivable':
        return 'bg-[#A9D342]/15 text-[#A9D342] border border-[#A9D342]/30'
      case 'retention':
      case 'forced_retention':
        return 'bg-amber-500/15 text-amber-400 border border-amber-400/30'
      case 'available':
        return 'bg-sky-500/15 text-sky-400 border border-sky-400/30'
      default:
        return 'bg-zinc-700/30 text-zinc-300 border border-zinc-600/40'
    }
  }

  const { forecasts, isLoading, isRefetching } = useFetchForecasts({
    currentPage,
    start_date: dateRange?.from,
    end_date: dateRange?.to,
    currency,
  })

  const headers = useMemo(
    () => [
      { title: t('releaseDate'), key: 'date' },
      { title: 'Tipo', key: 'type' },
      { title: t('transaction'), key: 'transaction' },
      { title: t('value'), key: 'value' },
    ],
    [t],
  )

  const chartData = forecasts?.reports?.length
    ? forecasts.reports.map((report: any) => {
      const releaseLabel =
        report.release_amount_formatted ??
        report.release_formatted ??
        report.total ??
        ''
      const retentionLabel =
        report.retention_amount_formatted ?? report.retention_formatted ?? ''

      const releaseValue = parseMonetaryValue(releaseLabel)
      const retentionValue = parseMonetaryValue(retentionLabel)

      return {
        date: report.date ?? report.scheduled_at_formatted ?? '',
        label: report.total ?? releaseLabel ?? '',
        release_label: releaseLabel || '-',
        retention_label: retentionLabel || '-',
        value: report.total
          ? parseMonetaryValue(report.total)
          : releaseValue + retentionValue,
      }
    })
    : [
      { date: '', value: 0, label: '' },
      { date: '', value: 0, label: '' },
    ]

  return (
    <div className="space-y-4">
      <Card>
        <div className="flex gap-3 px-4 py-5 items-center flex-wrap justify-between border-b flex-row">
          <div className="flex items-center gap-3 max-sm:w-full">
            <h3 className="text-base font-semibold">{t('balanceForecast')}</h3>
            <Badge
              variant="secondary"
              className="text-xs font-medium gap-2 flex items-center justify-center"
            >
              {t('perDay')}
            </Badge>
          </div>
          <DateRangePicker
            direction="future"
            onChange={(range) => {
              setDateRange({
                from: range?.from?.toISOString(),
                to: range?.to?.toISOString(),
              })
            }}
          />
        </div>

        <div className="h-[252px] w-full">
          {isLoading || isRefetching ? (
            <Skeleton className="w-full h-full" />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#27272A"
                />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#71717A', fontSize: 12 }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#71717A', fontSize: 12 }}
                  tickFormatter={formatYAxisValue}
                  domain={['dataMin', 'dataMax']}
                />
                <Tooltip content={<CustomTooltip />} />
                <Line
                  type="linear"
                  dataKey="value"
                  stroke="#A9D342"
                  strokeWidth={2}
                  dot={false}
                  activeDot={{
                    r: 4,
                    fill: '#A9D342',
                    strokeWidth: 2,
                    stroke: '#18181B',
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>
      </Card>

      <Card>
        <div className="px-4 py-5 border-b border-b-colorBorder">
          <h3 className="text-base font-semibold">{t('transactions')}</h3>
        </div>

        <TableListCustom
          emptyDescriptionMessage={t('checkFilters')}
          emptyIconMessage={<Search />}
          emptyTitleMessage={t('noRecordsFound')}
          headers={headers}
          isLoadingData={isLoading || isRefetching}
          data={forecasts}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
        >
          {forecasts?.data?.map((transaction, index) => {
            const datePart = transaction.scheduled_at_formatted?.split(' ')[0]
            const forcedScheduledAt = datePart ? `${datePart} 22:00` : transaction.scheduled_at_formatted

            return (
              <TableRow
                key={index}
                className="hover:bg-transparent border-t border-zinc-800 whitespace-nowrap"
              >
                <TableCell className="py-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[#A1A1AA]">
                      {forcedScheduledAt}
                    </span>
                    <span className="text-xs text-zinc-500">
                      ({getRelativeTime(forcedScheduledAt, t)})
                    </span>
                  </div>
                </TableCell>
                <TableCell className="py-4 text-[#A1A1AA]">
                  <Badge
                    variant="secondary"
                    className={getEntryTypeBadgeClassName(transaction.entry_type)}
                  >
                    {isReceivableEntryType(transaction.entry_type)
                      ? t('receivable')
                      : isRetentionEntryType(transaction.entry_type)
                        ? t('retention')
                        : t('available')}
                  </Badge>
                </TableCell>
                <TableCell className="py-4 text-[#A1A1AA]">
                  {transaction.order_uuid && (
                    <span 
                      className="cursor-pointer hover:underline text-primary font-mono"
                      onClick={() => navigate(`/sales/sales-details/${transaction.order_uuid}`)}
                    >
                      {transaction.order_uuid}
                    </span>
                  )}
                </TableCell>
                <TableCell className="py-4">
                  {isReceivableEntryType(transaction.entry_type)
                    ? transaction.release_amount_formatted
                    : isRetentionEntryType(transaction.entry_type)
                      ? transaction.retention_amount_formatted
                      : transaction.amount_formatted}
                </TableCell>
              </TableRow>
            )
          })}
        </TableListCustom>
      </Card>
    </div>
  )
}
